import { Box } from '@mui/material';
import { Map as OLMap } from 'ol';
import { Coordinate } from 'ol/coordinate';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat } from 'ol/proj';
import React from 'react';

import { useCurrentActor } from '../../hooks/useCurrentActor';
import useEventsLayer from '../../hooks/useEventsLayer';
import useJSON from '../../hooks/useJSON';
import useModalDispatch from '../../hooks/useModalDispatch';
import usePostalTileLayer from '../../hooks/usePostalTileLayer';
import useSelectedPostalCode from '../../hooks/useSelectedPostalCode';
import EventModal from '../../modals/EventModal';
import EventsModal from '../../modals/EventsModal';
import PostalCodeOverlay from '../../models/postalCodes/PostalCodeOverlay';
import { LoadingCard } from '../Loading';
import Spinner from '../Loading/Spinner';

import MapCanvas from './components/MapCanvas';
import { FOCUS_ZOOM } from './hooks/useMap';
import useMapboxTileUrl from './hooks/useMapboxTileUrl';
import {
  ClusterSelectCallback,
  FeatureSelectCallback,
  MapVariant,
  MapViewChangeCallback,
  ViewProps,
} from './lib/types';

const DEFAULT_LAT = 52.1344;
const DEFAULT_LON = 5.1917;
const DEFAULT_ZOOM = 6.8;

export interface Event {
  image: string;
  id: string;
  lat: number;
  lon: number;
}

export interface EventStats {
  events: Event[];
}

export interface GlappMapProps {
  selectedPostalCode?: number;
  map?: OLMap;
  setMap?: (map: OLMap) => void;
  setSelectedPostalCode: (postalDigits?: number) => void;
}

const GlappMap: React.FC<GlappMapProps> = ({ map, selectedPostalCode, setMap, setSelectedPostalCode }) => {
  const dispatchEventModal = useModalDispatch(EventModal);
  const dispatchEventsModal = useModalDispatch(EventsModal);
  const { centerLat, centerLon, zoom } = useCurrentActor();
  const [view, setView] = React.useState<ViewProps>({
    center: fromLonLat([centerLon ?? DEFAULT_LON, centerLat ?? DEFAULT_LAT]),
    zoom: zoom ?? DEFAULT_ZOOM,
  });
  const [eventStats, loadingEvents] = useJSON<EventStats>('event_stats.json');
  const [overlayPosition, setOverlayPosition] = React.useState<Coordinate | undefined>(undefined);
  const boundaryFeature = useSelectedPostalCode({
    map,
    selectedPostalCode,
    setOverlayPosition,
    setView,
    view,
  });
  const eventsLayer = useEventsLayer(eventStats?.events ?? []);
  const layers = React.useMemo(
    () => [{ features: boundaryFeature ? [boundaryFeature] : [] }, eventsLayer],
    [boundaryFeature, eventsLayer],
  );
  const mapboxTileURL = useMapboxTileUrl();
  const handleClusterSelect = React.useCallback<ClusterSelectCallback>(
    (features, newCenter) => {
      const [lon, lat] = toLonLat((features[0].getGeometry() as Point).getCoordinates());
      dispatchEventsModal({ lat, lon });
      setSelectedPostalCode(undefined);
      setOverlayPosition(newCenter);
    },
    [setSelectedPostalCode, setOverlayPosition],
  );
  const handleSelect = React.useCallback<FeatureSelectCallback>(
    (feature, newCenter) => {
      const { postal_digits: postalDigits, postcode5 } = feature?.getProperties() || {};

      if (postalDigits) {
        setSelectedPostalCode(postalDigits);
        if (newCenter) {
          setOverlayPosition(newCenter);
        }
      } else if (postcode5) {
        setSelectedPostalCode(parseInt(postcode5.slice(0, -1)));
        if (newCenter) {
          setOverlayPosition(newCenter);
        }
      } else {
        const id = feature?.getId();
        if (id) {
          dispatchEventModal({ eventId: id.toString() });
        }

        if (newCenter) {
          setOverlayPosition(newCenter);
          setView({
            center: newCenter,
            zoom: Math.max(view?.zoom || 0, feature ? FOCUS_ZOOM : 0),
          });
        }

        setSelectedPostalCode(undefined);
      }
    },
    [setSelectedPostalCode, view.zoom],
  );
  const handleViewChange = React.useCallback<MapViewChangeCallback>(
    (newCenter, newZoom) => {
      setView({
        center: newCenter,
        zoom: newZoom,
      });
    },
    [setView],
  );
  usePostalTileLayer(map, handleSelect);

  const overlay = selectedPostalCode ? (
    <PostalCodeOverlay id={selectedPostalCode.toString()} onClose={() => handleSelect(undefined)} />
  ) : undefined;

  if (!mapboxTileURL) {
    return <LoadingCard />;
  }

  return (
    <div>
      <MapCanvas
        layers={layers}
        mapboxTileURL={mapboxTileURL}
        overlay={overlay}
        overlayPosition={overlayPosition}
        setMap={setMap}
        variant={MapVariant.Fullscreen}
        view={view}
        onClusterSelect={handleClusterSelect}
        onSelect={handleSelect}
        onViewChange={handleViewChange}
      />
      {loadingEvents && (
        <Box overflow="hidden" position="absolute" right={0} top={0} zIndex={999}>
          <Spinner />
        </Box>
      )}
    </div>
  );
};

export default GlappMap;
